import { createRouter } from '@tanstack/react-router';
import { rootRoute } from './routes/root.js';
import { loginRoute } from './routes/login.js';
import { registerRoute } from './routes/register.js';
import { authedLayoutRoute } from './routes/authed.js';
import { dashboardRoute } from './routes/dashboard.js';
import { indexRoute } from './routes/index.js';
import { employeesRoute } from './routes/employees.js';
import { employeeNewRoute } from './routes/employeeNew.js';
import { employeeImportRoute } from './routes/employeeImport.js';
import { employeeDetailRoute } from './routes/employeeDetail.js';
import { payrollRunsRoute } from './routes/payrollRuns.js';
import { payrollRunNewRoute } from './routes/payrollRunNew.js';
import { payrollRunDetailRoute } from './routes/payrollRunDetail.js';
import { complianceReportsRoute } from './routes/complianceReports.js';
import { reportsHeadcountRoute } from './routes/reportsHeadcount.js';
import { reportsPayrollCostRoute } from './routes/reportsPayrollCost.js';
import { reportsLeaveLiabilityRoute } from './routes/reportsLeaveLiability.js';
import { reportsAbsenceRoute } from './routes/reportsAbsence.js';
import { reportsCostToCompanyRoute } from './routes/reportsCostToCompany.js';
import { complianceReportDetailRoute } from './routes/complianceReportDetail.js';
import { internationalRemoteRosterRoute } from './routes/internationalRemoteRoster.js';
import { exchangeRatesRoute } from './routes/exchangeRates.js';
import { meLayoutRoute } from './routes/meLayout.js';
import { meIndexRoute } from './routes/me.js';
import { myDocumentsRoute } from './routes/myDocuments.js';
import { myLeaveRoute } from './routes/myLeave.js';
import { myLoansRoute } from './routes/myLoans.js';
import { myLoansRequestRoute } from './routes/myLoansRequest.js';
import { myEwaDrawsRoute } from './routes/myEwaDraws.js';
import { myPayslipsRoute } from './routes/myPayslips.js';
import { employeeAdvancesRoute } from './routes/employeeAdvances.js';
import { loansRequestsRoute } from './routes/loansRequests.js';
import { loansPortfolioRoute } from './routes/loansPortfolio.js';
import { myProfileRoute } from './routes/myProfile.js';
import { securityRoute } from './routes/security.js';
import { bankProfileRoute } from './routes/bankProfile.js';
import { dataExportRoute } from './routes/dataExport.js';
import { securitySessionsRoute } from './routes/securitySessions.js';
import { leavePoliciesRoute } from './routes/leavePolicies.js';
import { leaveApprovalsRoute, meLeaveApprovalsRoute } from './routes/leaveApprovals.js';
import { leaveAdminRoute } from './routes/leaveAdmin.js';
import { overtimeApprovalsRoute, meOvertimeApprovalsRoute } from './routes/overtimeApprovals.js';
import { performanceAdminRoute } from './routes/performanceAdmin.js';
import { performanceGoalsRoute } from './routes/performanceGoals.js';
import { performanceReviewQueueRoute, mePerformanceReviewQueueRoute } from './routes/performanceReviewQueue.js';
import { performanceRollupRoute } from './routes/performanceRollup.js';
import { myPerformanceRoute } from './routes/myPerformance.js';
import { myAttendanceRoute } from './routes/myAttendance.js';
import { myTimesheetRoute } from './routes/myTimesheet.js';
import { attendanceTimesheetsRoute } from './routes/attendanceTimesheets.js';
import { attendanceImportRoute } from './routes/attendanceImport.js';
import { attendanceManualClockRoute } from './routes/attendanceManualClock.js';
import { attendanceConfigRoute } from './routes/attendanceConfig.js';
import { recruitmentRequisitionsRoute } from './routes/recruitmentRequisitions.js';
import { recruitmentApprovalsRoute, meRecruitmentApprovalsRoute } from './routes/recruitmentApprovals.js';
import { expenseApprovalsRoute, meExpenseApprovalsRoute } from './routes/expenseApprovals.js';
import { expensePortfolioRoute } from './routes/expensePortfolio.js';
import { billingRoute } from './routes/billing.js';
import { assetCatalogRoute } from './routes/assetCatalog.js';
import { assetCategoriesRoute } from './routes/assetCategories.js';
import { assetWorklistRoute } from './routes/assetWorklist.js';
import { assetImportRoute } from './routes/assetImport.js';
import { myAssetsRoute } from './routes/myAssets.js';
import { meAssetApprovalsRoute } from './routes/meAssetApprovals.js';
import { meAssetRequestsRoute } from './routes/meAssetRequests.js';
import { myExpenseClaimsRoute } from './routes/myExpenseClaims.js';
import { recruitmentPipelineRoute } from './routes/recruitmentPipeline.js';
import { recruitmentCandidatesRoute } from './routes/recruitmentCandidates.js';
import { recruitmentCandidateDetailRoute } from './routes/recruitmentCandidateDetail.js';
import { recruitmentImportRoute } from './routes/recruitmentImport.js';
import { recruitmentConvertRoute } from './routes/recruitmentConvert.js';
import { recruitmentRetentionRoute } from './routes/recruitmentRetention.js';
import { resetRequestRoute } from './routes/resetRequest.js';
import { resetConfirmRoute } from './routes/resetConfirm.js';
import { invitationAcceptRoute } from './routes/invitationAccept.js';
import { legalDataRetentionRoute } from './routes/legalDataRetention.js';
// Platform operator console (super-admin). Separate auth track + shell.
import { platformLayoutRoute } from './routes/platform/platformLayout.js';
import { platformLoginRoute } from './routes/platform/platformLogin.js';
import { platformIndexRoute } from './routes/platform/platformIndex.js';
import { platformTenantsRoute } from './routes/platform/platformTenants.js';
import { platformTenantDetailRoute } from './routes/platform/platformTenantDetail.js';
import { platformScheduledJobsRoute } from './routes/platform/platformScheduledJobs.js';
import { platformPaddleMappingsRoute } from './routes/platform/platformPaddleMappings.js';

/**
 * Assemble the route tree and instantiate the router.
 *
 * Tree:
 *   /                      → redirect to /login or /dashboard
 *   /login                 → LoginPage (redirects if already authed)
 *   /register              → RegisterPage (redirects if already authed)
 *   /(authed)              → AuthedLayout (redirects to /login if not authed)
 *     /dashboard
 *     /employees           → list
 *     /employees/new       → create form
 *     /employees/:id       → detail
 *     /payroll-runs        → list
 *     /payroll-runs/new    → create form (supports ?supersedesId= for corrections)
 *     /payroll-runs/:id    → detail with calculate/approve/void actions
 *
 * Literal paths are declared before dynamic ones (TanStack Router sorts by
 * specificity, but declaring the literal first keeps intent obvious).
 */
const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  authedLayoutRoute.addChildren([
    dashboardRoute,
    employeesRoute,
    employeeNewRoute,
    employeeImportRoute,
    employeeDetailRoute,
    employeeAdvancesRoute,
    loansPortfolioRoute,
    loansRequestsRoute,
    payrollRunsRoute,
    payrollRunNewRoute,
    payrollRunDetailRoute,
    complianceReportsRoute,
    reportsHeadcountRoute,
    reportsPayrollCostRoute,
    reportsLeaveLiabilityRoute,
    reportsAbsenceRoute,
    reportsCostToCompanyRoute,
    complianceReportDetailRoute,
    internationalRemoteRosterRoute,
    exchangeRatesRoute,
    leavePoliciesRoute,
    leaveApprovalsRoute,
    leaveAdminRoute,
    overtimeApprovalsRoute,
    performanceAdminRoute,
    performanceGoalsRoute,
    performanceReviewQueueRoute,
    performanceRollupRoute,
    attendanceTimesheetsRoute,
    attendanceImportRoute,
    attendanceManualClockRoute,
    attendanceConfigRoute,
    recruitmentRequisitionsRoute,
    recruitmentPipelineRoute,
    recruitmentApprovalsRoute,
    recruitmentCandidatesRoute,
    recruitmentImportRoute,
    recruitmentCandidateDetailRoute,
    recruitmentConvertRoute,
    recruitmentRetentionRoute,
    bankProfileRoute,
    dataExportRoute,
    expenseApprovalsRoute,
    expensePortfolioRoute,
    billingRoute,
    assetCatalogRoute,
    assetCategoriesRoute,
    assetWorklistRoute,
    assetImportRoute,
  ]),
  // Employee self-service portal — separate layout so the sidebar/
  // permissions are scoped to the employee role only. The layout's
  // beforeLoad bounces non-employee roles to /dashboard.
  meLayoutRoute.addChildren([
    meIndexRoute,
    myProfileRoute,
    myPayslipsRoute,
    myDocumentsRoute,
    myLeaveRoute,
    myLoansRoute,
    myLoansRequestRoute,
    myEwaDrawsRoute,
    meLeaveApprovalsRoute,
    meOvertimeApprovalsRoute,
    meRecruitmentApprovalsRoute,
    myExpenseClaimsRoute,
    meExpenseApprovalsRoute,
    mePerformanceReviewQueueRoute,
    myPerformanceRoute,
    myAttendanceRoute,
    myTimesheetRoute,
    myAssetsRoute,
    meAssetApprovalsRoute,
    meAssetRequestsRoute,
  ]),
  // /security is its own top-level route (any authenticated role). Mounting
  // it under authedLayoutRoute would lock employees out — that layout
  // actively bounces role=employee back to /me.
  securityRoute.addChildren([securitySessionsRoute]),
  // Unauthenticated reset-password pages. Top-level so they're reachable
  // without a session; each renders its own minimal chrome.
  resetRequestRoute,
  resetConfirmRoute,
  // Unauthenticated invitation-acceptance page (token IS the auth). Same
  // rationale: reachable without a session, renders its own chrome.
  invitationAcceptRoute,
  // Public application legal page — Data Retention & Deletion Policy (ADR-0021).
  // Top-level/unauthenticated so it's readable + linkable from login/register.
  legalDataRetentionRoute,
  // Platform operator console (super-admin). /platform/login is top-level
  // (reachable unauthenticated); the rest nest under the guarded layout.
  platformLoginRoute,
  platformLayoutRoute.addChildren([
    platformIndexRoute,
    platformScheduledJobsRoute,
    platformPaddleMappingsRoute,
    platformTenantsRoute,
    platformTenantDetailRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
